
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { PropType } from 'vue'
import type { Filter, RegularFilter, RegularFilterSelectedValues } from '../../models'
import type { FieldValueSelectMessages } from '../shared/field-value-select.types'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, watch } from 'vue'
import { useBrowseListFilter } from '../../composables/use-browse-list-filter'
import { useEndpoints } from '../../composables/use-endpoints'
import { mapGetters } from '../../composables/utils'
import { EndpointContextEnum, FeatureIdEnum, FilterIdEnum, FilterTypeEnum } from '../../enums'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import FieldValueSelect from '../shared/field-value-select.component.vue'

export default defineComponent({
  name: 'BrowseListFilter',
  components: {
    FieldValueSelect,
  },
  props: {
    filters: {
      type: Object as PropType<Record<string, Filter>>,
      default: () => ({}),
    },
  },
  setup(props) {
    const { proxy } = getCurrentInstance()!
    const { $epb, $store } = proxy

    const { currentInstance } = mapGetters<Instance>($store, CONFIG_CONSTS.moduleName, {
      currentInstance: CONFIG_CONSTS.getters.currentInstance,
    })

    const { endpoints } = useEndpoints()
    const { browseFilterValue, browseFieldOptions, setBrowseFilter, clearBrowseFilter } =
      useBrowseListFilter()

    const isEnabledRoute = computed(() => {
      const routeMeta = proxy.$route.meta
      if (!routeMeta.browseListFilterEnabled) {
        return false
      }
      if (routeMeta.featureId === FeatureIdEnum.CONFIGURATION) {
        return (
          currentInstance.value.domains[DomainEnum.SEARCH]?.tools[FeatureIdEnum.CONFIGURATION]
            ?.useSearchConfigurations ?? true
        )
      }
      return true
    })

    const isBrowseEndpointSelected = computed(() => {
      const endpointFilter = props.filters[FilterIdEnum.ENDPOINT]
      const selectedIds = endpointFilter
        ? (endpointFilter.selectedValues as RegularFilterSelectedValues)
        : endpoints.value.map(({ value }) => value)

      return endpoints.value.some(
        ({ value, context }) =>
          context === EndpointContextEnum.FILTER && selectedIds.includes(value),
      )
    })

    const visible = computed(
      () =>
        isEnabledRoute.value &&
        isBrowseEndpointSelected.value &&
        browseFieldOptions.value.length > 0,
    )

    watch(
      () =>
        (props.filters[FilterIdEnum.ENDPOINT]?.selectedValues ?? []) as RegularFilterSelectedValues,
      selectedValues => {
        const selectedEndpoint = endpoints.value.find(endpoint =>
          selectedValues.includes(endpoint.value),
        )
        if (selectedEndpoint?.context === EndpointContextEnum.FILTER) {
          setBrowseFilter({
            name: selectedEndpoint.defaultBrowseField,
            value: selectedEndpoint.defaultBrowseValue,
          })
        }
      },
      { immediate: true },
    )

    const pivots = computed(
      () =>
        Object.fromEntries(
          Object.entries(props.filters).filter(
            ([, { config }]) =>
              config.type === FilterTypeEnum.REGULAR &&
              config.id !== FilterIdEnum.ENDPOINT &&
              config.id !== FilterIdEnum.ENABLED,
          ),
        ) as Record<string, RegularFilter>,
    )

    const messages = computed<FieldValueSelectMessages>(() => {
      if (proxy.$route.meta.featureId === FeatureIdEnum.FACETS) {
        const { attribute, browse } = $epb.messages.tools.facets.custom
        return { name: attribute, value: browse }
      }
      const { browseField, browseValue } = $epb.messages.tools.configurations.fields
      return { name: browseField, value: browseValue }
    })

    watch(visible, isVisible => {
      if (!isVisible) {
        clearBrowseFilter()
      }
    })

    return {
      visible,
      pivots,
      messages,
      browseFilterValue,
      browseFieldOptions,
      setBrowseFilter,
    }
  },
})
